import { axiosAuth } from "services/generic/AxiosInterceptor/AxiosInterceptor"
import { makeFileInstance } from "services/generic/FileInstance"

import {
  CreatePhoneDeviceRequest,
  CreatePhoneTemplateRequest,
  EditPhoneDeviceRequest,
  EditPhoneGeneralSettingsRequest,
  EditPhoneTemplateRequest,
  InitialisePhoneSettingsRequest,
  PhoneScheduleOneTimeRequest,
  PhoneScheduleRecurringRequest,
  SetPhoneScheduleRecurringRequest,
} from "@shared/dtos/requests/phoneRequests.dto"
import {
  PhoneDeviceCreationResponse,
  PhoneDeviceResponse,
  PhoneGeneralSettingsResponse,
  PhoneScheduleCreationResponse,
  PhoneScheduleResponse,
  PhoneTemplateCreationResponse,
  PhoneTemplateResponse,
} from "@shared/dtos/responses/phoneResponses.dto"
import { AuthDeviceLinkRequest } from "@shared/zod/schemas/requests/phone"
import { AuthDeviceLinkResponse } from "@shared/zod/schemas/responses/phone"

export const initialise = async (practiceId: string, req: InitialisePhoneSettingsRequest): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/initialise`,
    method: "post",
    data: req,
  }).then(() => undefined)
}

export const getGeneralSettings = async (practiceId: string): Promise<PhoneGeneralSettingsResponse> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/general`,
    method: "get",
  }).then((resp) => {
    return resp.data
  })
}

export const editGeneralSettings = async (practiceId: string, req: EditPhoneGeneralSettingsRequest): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/general`,
    method: "patch",
    data: req,
  }).then(() => undefined)
}

export const getDevices = async (practiceId: string): Promise<PhoneDeviceResponse[]> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/devices`,
    method: "get",
  }).then((resp) => {
    return resp.data
  })
}

export const createDevice = async (
  practiceId: string,
  req: CreatePhoneDeviceRequest
): Promise<PhoneDeviceCreationResponse> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/devices`,
    method: "post",
    data: req,
  }).then((resp) => {
    return resp.data
  })
}

export const editDevice = async (practiceId: string, deviceId: string, req: EditPhoneDeviceRequest): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/devices/${deviceId}`,
    method: "patch",
    data: req,
  }).then(() => undefined)
}

export const deleteDevice = async (practiceId: string, deviceId: string): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/devices/${deviceId}`,
    method: "delete",
  }).then(() => undefined)
}

export const authDeviceLink = async (
  practiceId: string,
  deviceId: string,
  req: AuthDeviceLinkRequest
): Promise<AuthDeviceLinkResponse> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/devices/${deviceId}/auth-link`,
    method: "post",
    data: req,
  }).then((resp) => {
    return resp.data
  })
}

export const revokeDeviceAuth = async (practiceId: string, deviceId: string): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/devices/${deviceId}/auth-link`,
    method: "delete",
  }).then(() => undefined)
}

export const getTemplates = async (practiceId: string): Promise<PhoneTemplateResponse[]> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/templates`,
    method: "get",
  }).then((resp) => {
    return resp.data
  })
}

export const createTemplate = async (
  practiceId: string,
  req: CreatePhoneTemplateRequest
): Promise<PhoneTemplateCreationResponse> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/templates`,
    method: "post",
    data: req,
  }).then((resp) => {
    return resp.data
  })
}

export const editTemplate = async (
  practiceId: string,
  templateId: string,
  req: EditPhoneTemplateRequest
): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/templates/${templateId}`,
    method: "patch",
    data: req,
  }).then(() => undefined)
}

export const deleteTemplate = async (practiceId: string, templateId: string): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/templates/${templateId}`,
    method: "delete",
  }).then(() => undefined)
}

export const getSchedules = async (practiceId: string): Promise<PhoneScheduleResponse[]> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/schedules`,
    method: "get",
  }).then((resp) => {
    return resp.data
  })
}

/** TODO: Seems unused? Can we remove this */
export const replaceRecurringSchedules = async (
  practiceId: string,
  req: SetPhoneScheduleRecurringRequest
): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/schedules/recurring`,
    method: "put",
    data: req,
  }).then(() => undefined)
}

export const createSchedule = async (
  practiceId: string,
  req: PhoneScheduleRecurringRequest | PhoneScheduleOneTimeRequest
): Promise<PhoneScheduleCreationResponse> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/schedules`,
    method: "post",
    data: req,
  }).then((resp) => {
    return resp.data
  })
}

export const editSchedule = async (
  practiceId: string,
  scheduleId: string,
  req: PhoneScheduleRecurringRequest | PhoneScheduleOneTimeRequest
): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/schedules/${scheduleId}`,
    method: "put",
    data: req,
  }).then(() => undefined)
}

export const deleteSchedule = async (practiceId: string, scheduleId: string): Promise<void> => {
  return axiosAuth({
    url: `/phone/settings/${practiceId}/schedules/${scheduleId}`,
    method: "delete",
  }).then(() => undefined)
}

export const getAudio = async (audio: { fileId: string } | { text: string }) => {
  const query = "fileId" in audio ? `fileId=${audio.fileId}` : `text=${encodeURIComponent(audio.text)}`
  return axiosAuth({
    url: `/phone/settings/audio?${query}`,
    method: "get",
    responseType: "blob",
  }).then(makeFileInstance)
}
